import {Component, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {CoreService, PlanningService, Task} from 'proleis-rest-client';
import {combineLatest, debounceTime, exhaustMap, iif, Observable, of, shareReplay, tap} from 'rxjs';
import {ProleisObject} from 'proleis-rest-client/lib/models/proleis-object-models/proleis.object';
import {FormControl} from '@angular/forms';
import {startWith} from 'rxjs/operators';
import {BreakpointService, CurrentViewService} from "proleis-web-app";
import {PpsService} from "../../../../planning/components/pps/board/pps.service";

const SETTING_ASSIGNED_TASKS = 'p4m_selected_assigned_tasks';
const SETTINGS_EXCLUDE_FINISHED = 'p4m_selected_exclude_finished';
const SETTINGS_EXCLUDE_UNPLANNED = 'p4m_selected_exclude_unplanned';

@Component({
  selector: 'p4m-vc-info-panel',
  templateUrl: 'vc-infopanel.component.html',
  styleUrls: ['vc-infopanel.component.scss']
})
export class VcInfopanelComponent implements OnInit {
  vc$: Observable<ProleisObject>;
  tasks$: Observable<Task[]>;
  searchFormControl: FormControl;
  onlyAssignedTasks: FormControl = new FormControl<boolean>(localStorage.getItem(SETTING_ASSIGNED_TASKS) === 'true');
  includeFinished: FormControl = new FormControl<boolean>(localStorage.getItem(SETTINGS_EXCLUDE_FINISHED) === 'true');
  includeUnplanned: FormControl = new FormControl<boolean>(localStorage.getItem(SETTINGS_EXCLUDE_UNPLANNED) === 'true');
  isPhone$ = this.breakpointService.isSmall();

  constructor(
    private route: ActivatedRoute,
    private coreService: CoreService,
    private planningService: PlanningService,
    public currentViewService: CurrentViewService,
    private breakpointService: BreakpointService,
    public ppsService: PpsService
  ) {
    this.searchFormControl = new FormControl();
  }

  ngOnInit() {
    const filter$ = this.searchFormControl.valueChanges.pipe(startWith(''));

    this.tasks$ = this.getTasks();

    this.tasks$ = combineLatest([this.tasks$, filter$]).pipe(
      exhaustMap(([tasks, filter]) => {
        return iif(
          () => filter === '',
          of(tasks),
          this.getFilteredTasks(filter, tasks),
        );
      }),
    );

    this.subscribeToFormControlChanges();
  }

  private subscribeToFormControlChanges() {
    this.onlyAssignedTasks.valueChanges
      .pipe(
        shareReplay(1),
        debounceTime(250),
        tap((value) => {
          this.tasks$ = this.getTasks();
          localStorage.setItem(SETTING_ASSIGNED_TASKS, value.toString());
        }),
      )
      .subscribe();

    this.includeFinished.valueChanges
      .pipe(
        shareReplay(1),
        debounceTime(250),
        tap((value) => {
          this.tasks$ = this.getTasks();
          localStorage.setItem(SETTINGS_EXCLUDE_FINISHED, value.toString());
        })
      )
      .subscribe();

    this.includeUnplanned.valueChanges
      .pipe(
        shareReplay(1),
        debounceTime(250),
        tap((value) => {
          this.tasks$ = this.getTasks();
          localStorage.setItem(SETTINGS_EXCLUDE_UNPLANNED, value.toString());
        })
      ).subscribe();
  }

  getTasks() {
    const aliasFields =
      'CONTAINER_NAME=CONTAINERART_ID.NAME;CONTAINER_ICON=CONTAINERART_ID.CLIENTICON;' +
      'CONTAINER_STRANG=CONTAINER_STRANG_ID.TEXT;MS_CONT_NAME=MEILENSTEINCONT_ID.TEXT;TERM_ART_NAME=TERMINIERUNGSART_ID.TEXT' +
      'STD_BERECHNUNG=STD_BERECHNUNG_ID.TEXT';

    const aliasFieldsTask: string[] = ['INFOTEXT=OBJECT_ID.INFOTEXT', this.ppsService.getDynamicAliasFields(), this.ppsService.getHideIfAlias()];

    this.vc$ = this.coreService.getObjectById<ProleisObject>(
      this.route.snapshot.params.vcId,
      aliasFields,
    );
    return this.vc$.pipe(
      exhaustMap((vc) =>
        this.planningService.getTasks(
          vc.OBJECT_ID, this.onlyAssignedTasks.getRawValue(), !this.includeFinished.value,
          !this.includeUnplanned.value, aliasFieldsTask.join(';')
        )
      ),
    );
  }

  getFilteredTasks(filter: string, tasks: Task[]): Observable<Task[]> {
    if (tasks) {
      const headerFields = this.ppsService.getTaskComponentConfig().headerFields.map(field => field.field);
      const contentFields = this.ppsService.getTaskComponentConfig().contentFields.map(field => field.field);
      const fields = [...headerFields, ...contentFields];

      return of(
        tasks.filter((task) => {
          const filterExpression = filter.toUpperCase();
          return fields.some(field => task[field]?.toString().toUpperCase().includes(filterExpression));
        }),
      );
    }
    return of([])
  }

  updateTasks() {
    this.tasks$ = this.getTasks();
  }

  openDrawer() {
    this.currentViewService.drawer.toggle();
  }
}
